import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Boxes, Upload, Plus, Minus, Search, AlertTriangle, Database, Trash2, Loader2, PackagePlus, Pencil, Check, X, Shirt } from "lucide-react";

const COLORI = { bianco: "#f8fafc", nero: "#111827", grigio: "#9ca3af", "grigio melange": "#9ca3af", rosso: "#dc2626", "blu navy": "#1e3a8a", "blu royal": "#1d4ed8", blu: "#2563eb", navy: "#1e3a8a", azzurro: "#38bdf8", celeste: "#7dd3fc", verde: "#16a34a", "verde militare": "#4d7c0f", giallo: "#facc15", arancione: "#f97316", rosa: "#ec4899", fucsia: "#db2777", viola: "#7c3aed", marrone: "#92400e", beige: "#d6c7a1", sabbia: "#d6c7a1", panna: "#fdf6e3", bordeaux: "#7f1d1d" };
const getColor = (c) => COLORI[(c || "").trim().toLowerCase()] || "#64748b";

export default function GestioneMagazzino() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtro, setFiltro] = useState("tutti");
  const [importing, setImporting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingSoglia, setEditingSoglia] = useState(null); // { id, value }
  const [editingQta, setEditingQta] = useState(null); // { id, value }
  const [colorPreview, setColorPreview] = useState(null); // { colore, tipo, taglia, hex }
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filtro === "sotto_scorta") params.filtro = "sotto_scorta";
      const { data } = await api.get("/magazzino", { params });
      setItems(data);
    } catch (e) {
      toast.error("Errore nel caricamento del magazzino");
    } finally {
      setLoading(false);
    }
  }, [search, filtro]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const adjust = async (id, delta) => {
    try {
      const { data } = await api.post(`/magazzino/${id}/adjust`, { delta });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
    } catch (e) {
      toast.error("Errore nella modifica della giacenza");
    }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/magazzino/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Articolo eliminato");
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const saveSoglia = async (id) => {
    const val = Math.max(0, parseInt(editingSoglia?.value, 10) || 0);
    try {
      const { data } = await api.patch(`/magazzino/${id}`, { soglia_minima: val });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      setEditingSoglia(null);
      toast.success("Soglia minima aggiornata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'aggiornamento della soglia");
    }
  };

  const saveQta = async (id) => {
    const val = Math.max(0, parseInt(editingQta?.value, 10) || 0);
    try {
      const { data } = await api.patch(`/magazzino/${id}`, { quantita_attuale: val });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      setEditingQta(null);
      toast.success("Giacenza aggiornata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'aggiornamento della giacenza");
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/magazzino/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importazione completata: ${data.inseriti} nuovi, ${data.aggiornati} aggiornati`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'importazione");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const seedSample = async () => {
    try {
      const { data } = await api.post("/magazzino/seed-sample");
      toast.success(`${data.inseriti} articoli di esempio caricati`);
      load();
    } catch (e) {
      toast.error("Errore nel caricamento dati di esempio");
    }
  };

  return (
    <Layout>
      <input ref={fileRef} data-testid="file-import-input" type="file" accept=".csv,.xlsx,.xls" onChange={onImport} className="hidden" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-cyan-400 text-xs uppercase tracking-widest mb-1">
            <Boxes className="w-4 h-4" /> Capi neutri
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-100 tracking-tight">Gestione Magazzino</h1>
          <p className="text-slate-400 text-sm mt-1">Monitora giacenze e soglie minime dei capi neutri</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <>
              <button data-testid="seed-sample-btn" onClick={seedSample} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-700/40 text-sm">
                <Database className="w-4 h-4" /> Dati esempio
              </button>
              <button data-testid="add-item-btn" onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 text-sm">
                <PackagePlus className="w-4 h-4" /> Nuovo Articolo
              </button>
              <button data-testid="import-csv-btn" onClick={() => fileRef.current?.click()} disabled={importing}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm disabled:opacity-60">
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Importa Inventario da Excel/CSV
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="sku-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per SKU, tipo capo o colore..."
            className="w-full bg-slate-900/60 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-400/60 text-sm" />
        </div>
        <div className="flex gap-1.5">
          {[["tutti", "Tutti"], ["sotto_scorta", "Sotto Scorta"]].map(([val, lbl]) => (
            <button key={val} data-testid={`filter-${val}`} onClick={() => setFiltro(val)}
              className={`px-3.5 py-2.5 rounded-lg text-sm font-medium ${filtro === val ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/40" : "border border-slate-700 text-slate-400 hover:bg-slate-700/40"}`}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <div className="glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="magazzino-table">
            <thead>
              <tr className="border-b border-slate-700/60">
                {["SKU Neutro", "Tipo Capo", "Colore", "Taglia", "Giacenza", "Soglia Min.", "Stato", ...(isAdmin ? ["Azioni"] : [])].map((h) => (
                  <th key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-400 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">Magazzino vuoto. Importa un file o carica i dati di esempio.</td></tr>
              ) : (
                items.map((it) => (
                  <tr key={it.id} data-testid={`magazzino-row-${it.sku_neutro}`}
                    className={`border-b border-slate-800/60 transition-colors ${it.sotto_soglia ? "bg-red-500/10 border-l-4 border-l-red-500" : "hover:bg-slate-700/20"}`}>
                    <td className="px-4 py-3 font-mono-tech text-cyan-300 text-sm">{it.sku_neutro}</td>
                    <td className="px-4 py-3 text-slate-300 text-sm">{it.tipo_capo}</td>
                    <td className="px-4 py-3">
                      <button data-testid={`color-preview-${it.sku_neutro}`} onClick={() => setColorPreview({ colore: it.colore, tipo: it.tipo_capo, taglia: it.taglia, hex: getColor(it.colore) })}
                        className="flex items-center gap-2 text-slate-400 text-sm hover:text-slate-100 transition-colors" title="Mostra articolo nel colore">
                        <span className="w-4 h-4 rounded-full border border-slate-500 shrink-0" style={{ backgroundColor: getColor(it.colore) }} />
                        {it.colore}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-sm">{it.taglia}</td>
                    <td className="px-4 py-3">
                      {editingQta?.id === it.id ? (
                        <div className="flex items-center gap-1.5">
                          <input data-testid={`qta-input-${it.sku_neutro}`} type="number" min="0" autoFocus value={editingQta.value}
                            onChange={(e) => setEditingQta({ id: it.id, value: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") saveQta(it.id); if (e.key === "Escape") setEditingQta(null); }}
                            className="w-20 bg-slate-900/70 border border-cyan-400/50 rounded-md px-2 py-1 text-slate-100 text-sm font-mono-tech outline-none" />
                          <button data-testid={`qta-save-${it.sku_neutro}`} onClick={() => saveQta(it.id)} className="w-7 h-7 rounded-md bg-green-500/20 text-green-300 hover:bg-green-500/30 flex items-center justify-center"><Check className="w-4 h-4" /></button>
                          <button data-testid={`qta-cancel-${it.sku_neutro}`} onClick={() => setEditingQta(null)} className="w-7 h-7 rounded-md bg-slate-700/40 text-slate-400 hover:text-slate-200 flex items-center justify-center"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className={`font-mono-tech text-lg font-bold ${it.sotto_soglia ? "text-red-300" : "text-slate-100"}`} data-testid={`qty-${it.sku_neutro}`}>{it.quantita_attuale}</span>
                          {isAdmin && (
                            <button data-testid={`qta-edit-${it.sku_neutro}`} onClick={() => setEditingQta({ id: it.id, value: String(it.quantita_attuale) })} className="w-7 h-7 rounded-md bg-slate-700/40 text-slate-400 hover:text-cyan-300 flex items-center justify-center" title="Modifica quantità"><Pencil className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingSoglia?.id === it.id ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            data-testid={`soglia-input-${it.sku_neutro}`}
                            type="number"
                            min="0"
                            autoFocus
                            value={editingSoglia.value}
                            onChange={(e) => setEditingSoglia({ id: it.id, value: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") saveSoglia(it.id); if (e.key === "Escape") setEditingSoglia(null); }}
                            className="w-16 bg-slate-900/70 border border-cyan-400/50 rounded-md px-2 py-1 text-slate-100 text-sm font-mono-tech outline-none"
                          />
                          <button data-testid={`soglia-save-${it.sku_neutro}`} onClick={() => saveSoglia(it.id)} className="w-7 h-7 rounded-md bg-green-500/20 text-green-300 hover:bg-green-500/30 flex items-center justify-center"><Check className="w-4 h-4" /></button>
                          <button data-testid={`soglia-cancel-${it.sku_neutro}`} onClick={() => setEditingSoglia(null)} className="w-7 h-7 rounded-md bg-slate-700/40 text-slate-400 hover:text-slate-200 flex items-center justify-center"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono-tech text-slate-400 text-sm">{it.soglia_minima}</span>
                          {isAdmin && (
                            <button data-testid={`soglia-edit-${it.sku_neutro}`} onClick={() => setEditingSoglia({ id: it.id, value: String(it.soglia_minima) })} className="w-7 h-7 rounded-md bg-slate-700/40 text-slate-400 hover:text-cyan-300 flex items-center justify-center" title="Modifica soglia minima"><Pencil className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {it.sotto_soglia ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-300 bg-red-500/15 border border-red-500/40 px-2 py-1 rounded">
                          <AlertTriangle className="w-3 h-3" /> Sotto Scorta!
                        </span>
                      ) : (
                        <span className="text-xs font-semibold text-green-300 bg-green-500/15 border border-green-500/30 px-2 py-1 rounded">Disponibile</span>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button data-testid={`dec-${it.sku_neutro}`} onClick={() => adjust(it.id, -1)} className="w-8 h-8 rounded-md bg-red-500/15 text-red-300 hover:bg-red-500/25 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                          <button data-testid={`inc-${it.sku_neutro}`} onClick={() => adjust(it.id, 1)} className="w-8 h-8 rounded-md bg-green-500/15 text-green-300 hover:bg-green-500/25 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                          <button data-testid={`del-${it.sku_neutro}`} onClick={() => removeItem(it.id)} className="w-8 h-8 rounded-md bg-slate-700/40 text-slate-400 hover:bg-slate-600/60 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {colorPreview && (
        <div data-testid="color-preview-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setColorPreview(null)}>
          <div className="glass rounded-2xl p-6 w-full max-w-xs fade-in text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-semibold text-slate-100 mb-1">{colorPreview.tipo}</h3>
            <p className="text-slate-500 text-sm mb-4">{colorPreview.colore} · Taglia {colorPreview.taglia}</p>
            <div className="flex items-center justify-center py-4">
              <Shirt className="w-28 h-28" strokeWidth={1.2} style={{ color: colorPreview.hex }} />
            </div>
            <div className="flex items-center justify-center gap-2 mt-2">
              <span className="w-5 h-5 rounded-full border border-slate-500" style={{ backgroundColor: colorPreview.hex }} />
              <span className="text-slate-300 text-sm">{colorPreview.colore}</span>
            </div>
            <button data-testid="color-preview-close" onClick={() => setColorPreview(null)} className="mt-5 w-full py-2.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm">Chiudi</button>
          </div>
        </div>
      )}
      {showAdd && <AddModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </Layout>
  );
}

const AddModal = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ sku_neutro: "", tipo_capo: "T-shirt", colore: "", taglia: "", quantita_attuale: 0, soglia_minima: 0 });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/magazzino", {
        ...form,
        quantita_attuale: Number(form.quantita_attuale),
        soglia_minima: Number(form.soglia_minima),
      });
      toast.success("Articolo aggiunto");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="glass rounded-2xl p-6 w-full max-w-md fade-in" onClick={(e) => e.stopPropagation()} data-testid="add-item-modal">
        <h3 className="font-heading text-lg font-semibold text-slate-100 mb-4">Nuovo Articolo Neutro</h3>
        <div className="space-y-3">
          {[["sku_neutro", "SKU Neutro (es. TSA-BIA-S)", "text"], ["colore", "Colore", "text"], ["taglia", "Taglia", "text"]].map(([k, lbl, type]) => (
            <div key={k}>
              <label className="text-xs text-slate-400 uppercase tracking-wider">{lbl}</label>
              <input data-testid={`add-${k}`} type={type} value={form[k]} onChange={(e) => set(k, e.target.value)}
                className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-cyan-400/60 text-sm" />
            </div>
          ))}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider">Tipo Capo</label>
            <select data-testid="add-tipo_capo" value={form.tipo_capo} onChange={(e) => set("tipo_capo", e.target.value)}
              className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-cyan-400/60 text-sm">
              {["T-shirt", "Felpa Zip", "Canotta", "Cappello", "Cappello Pescatore", "Berretto", "Polo", "Body Baby"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[["quantita_attuale", "Giacenza"], ["soglia_minima", "Soglia Minima"]].map(([k, lbl]) => (
              <div key={k}>
                <label className="text-xs text-slate-400 uppercase tracking-wider">{lbl}</label>
                <input data-testid={`add-${k}`} type="number" value={form[k]} onChange={(e) => set(k, e.target.value)}
                  className="mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-cyan-400/60 text-sm" />
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-700/40 text-sm">Annulla</button>
          <button data-testid="add-save-btn" onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salva
          </button>
        </div>
      </div>
    </div>
  );
};
